import React, { Component, Fragment, lazy, Suspense } from 'react';

import { ErrorBoundary } from 'common/components/ErrorBoundary';
import { GlobalErrorHandler } from 'common/components/GlobalErrorHandler';

import * as singleSpa from 'single-spa';
import * as ClassicApp from 'singlespa/apps/classic';

import { domReady, initializeLayers } from './doc-init';

import Perf, { PerfKeys } from 'common/debug/perf';
import { isGammaHeaderEnabled } from 'common/lib/feature-flag-client';
import Storage from 'common/lib/storage';
import Token from 'common/lib/util/token';

import API from 'gamma/api';
import { ComponentWrapper } from 'gamma/components/ComponentWrapper';
import LayerManager from 'gamma/components/layer-manager';
import Analytics, { buildContextGetter } from 'gamma/debug/analytics';
import defaultStore from 'gamma/defaultStore';
import { setMember } from 'gamma/modules';
import experiment from 'gamma/util/experiments';

const GammaHeaderProvider = lazy(() => {
  // tslint:disable:max-line-length
  return import(/* webpackChunkName: "gamma-header-provider" */ 'gamma/HeaderProvider');
  // tslint:enable:max-line-length
});

// Find this block of imperative setup a better home :(
const { store } = defaultStore;
API.setStore(store);
experiment.setStore(store);
Analytics.setAppContextGetter(buildContextGetter(store));
Storage.setStore(store);
store.dispatch(setMember({ idMe: Token.getMyId() }));

const FooterUrls = {
  HELP:
    'http://help.trello.com/article/736-troubleshooting-browser-issues-with-trello',
  CONTACT: '/contact?context=load-failure&source=from%20in%20app%20footer',
};

class StaticChrome extends Component {
  constructor(props) {
    super(props);

    domReady();
  }

  componentDidMount() {
    initializeLayers();
    // Tell single-spa that you're ready for it to mount your application to the DOM
    ClassicApp.setElem(document.getElementById('classic'));
    singleSpa.start();
    Perf.stopMeasurement(PerfKeys.FIRST_RENDER);
  }

  render() {
    const { isGammaHeader, gammaHeaderComponents } = this.props;
    const {
      Header,
      Alerts,
      Overlays,
      BoardsMenu,
      LogoutTrigger,
    } = gammaHeaderComponents;

    return (
      <ErrorBoundary errorHandlerComponent={GlobalErrorHandler}>
        <LayerManager>
          {isGammaHeader ? (
            <ComponentWrapper>
              <BoardsMenu />
            </ComponentWrapper>
          ) : (
            <div id="boards-drawer" />
          )}
          <p id="notification" />
          <div id="surface">
            {isGammaHeader ? (
              <ComponentWrapper>
                <Header />
              </ComponentWrapper>
            ) : (
              <div id="header" />
            )}
            <div id="banners" />
            <div id="content" />
            <div id="footer">
              <div id="contact-us" style={{ display: 'none' }}>
                If you're having trouble loading Trello,{' '}
                <a href={FooterUrls.HELP} target="support">
                  check out our troubleshooting guide
                </a>{' '}
                or{' '}
                <a href={FooterUrls.CONTACT} target="contact">
                  contact us
                </a>!
              </div>
              <div id="footer-chrome" style={{ display: 'none' }} />
            </div>
          </div>
          {isGammaHeader && (
            <ComponentWrapper>
              <Fragment>
                <Alerts />
                <Overlays />
                <LogoutTrigger />
              </Fragment>
            </ComponentWrapper>
          )}
        </LayerManager>
        <div className="window-overlay">
          <div className="window">
            <a className="focus-dummy" href="#" />
            <div className="window-wrapper" />
          </div>
        </div>
        <div className="pop-over" />
        <div className="tooltip-container" />
        <div id="clipboard-container" />
      </ErrorBoundary>
    );
  }
}

export const Chrome = () => {
  // StaticChrome exists as a separate component because Chrome renders
  // asynchronously (in the case of the Gamma header), and we need to be
  // certain that the static HTML is rendered to the page before the life-
  // cycle methods run (componentDidMount initializes some Classic code
  // that selects and caches elements, so we need them to be present).
  const isGammaHeader = isGammaHeaderEnabled();

  return isGammaHeader ? (
    <Suspense fallback={null}>
      <GammaHeaderProvider>
        {(module) => (
          <StaticChrome
            isGammaHeader={true}
            gammaHeaderComponents={{ ...module }}
          />
        )}
      </GammaHeaderProvider>
    </Suspense>
  ) : (
    <StaticChrome isGammaHeader={false} gammaHeaderComponents={{}} />
  );
};



// WEBPACK FOOTER //
// ./app/scripts/views/chrome/chrome-view.js